import { PrintLayoutComponent } from './layouts/print-layout/print-layout.component';
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Routes, RouterModule } from '@angular/router';
import { NgModule } from '@angular/core';

import { DetailsComponent } from './pages/details/details.component';
import { LoginPageComponent } from './pages/login/login.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';

import { RegisterComponent } from './pages/details/register/register/register.component';
import { OpenSearchComponent } from './pages/search/open-search/open-search/open-search.component';
import { GiploginComponent } from './pages/login/giplogin/giplogin.component';
import { SamlComponent } from './pages/login/saml/saml.component';

// Guards
import { AuthGuard, AuthGuardStaff } from './core/guards/auth.guard';
import { LoginGuard } from './core/guards/login.guard';
import { RequestGuard } from './core/guards/request.guard';
import { MainV6LayoutComponent } from './layouts/main-v6-layout/main-v6-layout.component';
import { WidgetLayoutComponent } from './layouts/widget-layout/widget-layout.component';

export const routes: Routes = [
  {
    component: MainV6LayoutComponent,
    path: '',
    canActivate: [LoginGuard],
    children: [
      // lazy loaded modules
      {
        path: '',
        loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
      },
      {
        path: 'home',
        loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
      },
      {
        path: 'auth',
        loadChildren: () =>
          import('./components/forgot-your-password/forgot-your-password.module').then(
            (m) => m.ForgotYourPasswordModule,
          ),
      },
      {
        path: 'account',
        loadChildren: () =>
          import('./components/your-settings/your-settings.module').then(
            (m) => m.YourSettingsModule,
          ),
      },
      {
        path: 'advancedsearch',
        loadChildren: () =>
          import('./pages/advancedsearch/advancedsearch.module').then(
            (m) => m.AdvancedsearchModule,
          ),
      },
      {
        path: 'brief',
        loadChildren: () => import('./pages/brief/brief.module').then((m) => m.BriefModule),
      },
      {
        path: 'briefShareit',
        loadChildren: () =>
          import('./pages/brief-shareit/brief-shareit.module').then((m) => m.BriefShareitModule),
      },
      {
        path: 'browse',
        loadChildren: () => import('./pages/browse/browse.module').then((m) => m.BrowseModule),
      },
      {
        path: 'bsu',
        loadChildren: () =>
          import('./pages/batchstatusupdate/batchstatusupdate.module').then(
            (m) => m.BatchStatusUpdateModule,
          ),
      },
      {
        path: 'circulation',
        loadChildren: () =>
          import('./pages/circulation/circulation.module').then((m) => m.CirculationModule),
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./pages/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: 'edit-footer',
        loadChildren: () =>
          import('./pages/edit-footer/edit-footer.module').then((m) => m.EditFooterModule),
      },
      {
        path: 'ezproxy',
        loadChildren: () => import('./pages/ezproxy/ezproxy.module').then((m) => m.EzproxyModule),
      },
      {
        path: 'hold',
        loadChildren: () => import('./pages/hold/hold.module').then((m) => m.HoldModule),
      },
      {
        path: 'home-edit',
        loadChildren: () =>
          import('./pages/home-edit/home-edit.module').then((m) => m.HomeEditModule),
      },
      {
        path: 'ill',
        loadChildren: () =>
          import('./components/searches-with-zero-results/searches-with-zero-results.module').then(
            (m) => m.SearchesWithZeroResultsModule,
          ),
      },
      {
        path: 'items',
        loadChildren: () =>
          import('./components/item-details/your-items.module').then((m) => m.YourItemsModule),
      },
      {
        path: 'kids',
        loadChildren: () =>
          import('./pages/kids-catalog-search/kids-catalog-search.module').then(
            (m) => m.KidsCatalogSearchModule,
          ),
      },
      {
        path: 'list',
        loadChildren: () =>
          import('./pages/your-list/your-list.module').then((m) => m.YourListModule),
      },
      {
        path: 'marc',
        loadChildren: () => import('./pages/marc/marc.module').then((m) => m.MarcModule),
        canActivate: [AuthGuard, AuthGuardStaff],
      },
      {
        path: 'shippingLabels',
        loadChildren: () =>
          import('./pages/shipping-labels/shipping-labels.module').then(
            (m) => m.ShippingLabelsModule,
          ),
      },
      {
        path: 'reports',
        loadChildren: () => import('./pages/reports/reports.module').then((m) => m.ReportsModule),
        canActivate: [AuthGuardStaff],
      },
      {
        path: 'page',
        loadChildren: () => import('./pages/page/page.module').then((m) => m.PageModule),
      },
      {
        path: 'PatronLookupTool',
        loadChildren: () =>
          import('./pages/patron-lookup/patron-lookup.module').then((m) => m.PatronLookupModule),
      },
      {
        path: 'request/blank',
        loadChildren: () => import('./pages/request/request.module').then((m) => m.RequestModule),
        canActivate: [RequestGuard],
      },
      {
        path: 'request',
        loadChildren: () => import('./pages/request/request.module').then((m) => m.RequestModule),
        canActivate: [RequestGuard],
      },
      {
        path: 'request-edit',
        loadChildren: () =>
          import('./pages/request-edit/request-edit.module').then((m) => m.RequestEditModule),
      },
      {
        path: 'search',
        loadChildren: () => import('./pages/search/search.module').then((m) => m.SearchModule),
      },
      {
        path: 'signup',
        loadChildren: () => import('./pages/signup/signup.module').then((m) => m.SignupModule),
      },
      {
        path: 'userinfo',
        loadChildren: () =>
          import('./pages/userinfo/userinfo.module').then((m) => m.UserinfoModule),
      },
      {
        path: 'webLinks',
        loadChildren: () =>
          import('./pages/web-links/web-links.module').then((m) => m.WebLinksModule),
      },
      {
        path: 'acquisitions',
        loadChildren: () =>
          import('./pages/acquisitions/acquisitions.module').then((m) => m.AcquisitionsModule),
      },
      {
        path: 'holdings',
        loadChildren: () =>
          import('./pages/holdings/holdings.module').then((m) => m.HoldingsModule),
      },
      {
        path: 'staff',
        loadChildren: () => import('./pages/staff/staff.module').then((m) => m.StaffModule),
      },
      // regular routes without lazy loading
      { path: 'details', component: DetailsComponent, canActivate: [AuthGuard] },
      { path: 'record', redirectTo: 'details' },
      { path: 'login', component: LoginPageComponent },
      { path: 'giplogin', component: GiploginComponent },
      { path: 'loginsso', component: SamlComponent },
      { path: 'register', component: RegisterComponent, canActivate: [AuthGuard] },
      { path: 'sch', component: OpenSearchComponent },
      { path: '404', component: PageNotFoundComponent },
    ],
  },
  {
    component: WidgetLayoutComponent,
    canActivate: [LoginGuard],
    path: 'page-widgets',
    loadChildren: () =>
      import('./pages/page-widgets/page-widgets.module').then((m) => m.PageWidgetsModule),
  },
  {
    component: PrintLayoutComponent,
    canActivate: [LoginGuard],
    path: '',
    loadChildren: () => import('./modules/print-module/print-module').then((m) => m.PrintModule),
  },
  // Catch All
  {
    component: MainV6LayoutComponent,
    path: '',
    canActivate: [LoginGuard],
    children: [{ path: '**', component: PageNotFoundComponent }],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
